/**
 * The time estimator (DESIGN UD-11, UX-SPEC §15a): one pure module, no DOM, no clock of its own (the caller passes `now`).
 *
 * Method (W7c handoff, choice 1):
 *  - Before any progress, a prior from the word count and the reported rates (`rates.ts`) when the caller has one.
 *  - After that, an extrapolation from the fraction done and the elapsed time. The prior fades out as the fraction grows
 *    (full weight on the progress at 30% done).
 *  - The estimate is smoothed (an exponential average) and ratcheted: while progress is steady the remainder only goes
 *    down, so the rounded label never counts up. It rises only when the new figure is clearly higher (more than 1.5 times
 *    the shown remainder plus 5 s), which is a genuine slowdown.
 *  - No estimate during the first 3 s, never a negative remainder, and no number at all when it would be a guess: a prior
 *    that was exceeded by a quarter, or no prior and no progress. The caller then shows only the working indicator.
 *  - The label is rounded: seconds to the nearest 5 up to a minute, then whole minutes, then whole hours.
 */
import { fmtInt } from './format.ts';
import { DURATION } from './strings-explain.ts';
import { WORKING } from './strings.ts';

export const QUIET_MS = 3000;
const SMOOTHING = 0.3;
/** A genuine slowdown is followed faster than a speed-up: an underestimate misleads more than an overestimate. */
const RISE_SMOOTHING = 0.5;
const FULL_PROGRESS_WEIGHT_AT = 0.3;
/** Progress that has moved less than this is too little to extrapolate from. */
const MIN_MOVED = 0.02;
const RISE_FACTOR = 1.5;
const RISE_SLACK_MS = 5000;
/** A prior that the run overshoots by this factor is dropped: "taking longer than expected". */
const PRIOR_OVERRUN = 1.25;
/** Nothing past two days is shown as a number. */
const MAX_REMAINING_MS = 48 * 3_600_000;

export interface EstimatorOptions {
	/** Same clock as `update`'s `now`. */
	startedAt: number;
	/** The fraction already done when this estimator starts (a resumed job); default 0. */
	startFraction?: number;
	/** The expected total duration from the word count and the reported rates; null or absent: no prior. */
	priorMs?: number | null;
	/** No estimate before this long; default 3 s. */
	quietMs?: number;
}

export interface Estimate {
	/** The smoothed remainder, never negative. */
	remainingMs: number;
	/** The rounded text ("about 2 minutes left"). */
	text: string;
}

export interface Estimator {
	/** `fraction` is 0..1, or null when the operation reports no progress. Returns null while no honest estimate exists. */
	update(now: number, fraction: number | null): Estimate | null;
}

/** The rounded label of a remainder. Monotone non-decreasing in `ms`, so a falling remainder never makes the label rise. */
export function remainingText(ms: number): string {
	const s = Math.max(0, ms) / 1000;
	const L = WORKING.left;
	if (s < 8) return L.fewSeconds;
	if (s < 53) return L.seconds(fmtInt(Math.max(10, Math.round(s / 5) * 5)));
	if (s < 90) return L.minute;
	const min = s / 60;
	if (min < 55) return L.minutes(fmtInt(Math.round(min)));
	const h = min / 60;
	if (h < 1.5) return L.hour;
	return L.hours(fmtInt(Math.round(h)));
}

/** A duration in the same rounding as `remainingText`, without the "left" ("40 seconds", "2 minutes"). */
export function durationText(ms: number): string {
	const s = Math.max(0, ms) / 1000;
	if (s < 8) return DURATION.fewSeconds;
	if (s < 53) return DURATION.seconds(fmtInt(Math.max(10, Math.round(s / 5) * 5)));
	if (s < 90) return DURATION.minute;
	const min = s / 60;
	if (min < 55) return DURATION.minutes(fmtInt(Math.round(min)));
	const h = min / 60;
	if (h < 1.5) return DURATION.hour;
	return DURATION.hours(fmtInt(Math.round(h)));
}

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

export function createEstimator(opts: EstimatorOptions): Estimator {
	const quiet = opts.quietMs ?? QUIET_MS;
	const f0 = clamp01(opts.startFraction ?? 0);
	const prior =
		opts.priorMs !== undefined && opts.priorMs !== null && opts.priorMs > 0 ? opts.priorMs : null;
	let shown: number | null = null;
	let lastNow = opts.startedAt;

	return {
		update(now, fraction) {
			const elapsed = Math.max(0, now - opts.startedAt);
			const dt = Math.max(0, now - lastNow);
			lastNow = now;
			if (elapsed < quiet) return null;

			const f = fraction === null ? null : Math.max(f0, clamp01(fraction));
			const moved = f === null ? 0 : f - f0;
			if (f !== null && f >= 1) {
				shown = 0;
				return { remainingMs: 0, text: remainingText(0) };
			}

			// the two figures
			const fromProgress = moved >= MIN_MOVED && f !== null ? (elapsed * (1 - f)) / moved : null;
			let fromPrior: number | null = null;
			if (prior !== null) {
				// the prior's remainder: what is left of the time, scaled by the fraction when there is one
				const left = f !== null ? prior * (1 - f) : prior - elapsed;
				fromPrior = Math.max(0, left);
				if (f === null && elapsed > prior * PRIOR_OVERRUN) fromPrior = null; // overshot: no honest number
				if (f !== null && moved <= 0 && elapsed > prior * PRIOR_OVERRUN) fromPrior = null;
			}
			let target: number | null;
			if (fromProgress !== null && fromPrior !== null) {
				const w = clamp01(moved / FULL_PROGRESS_WEIGHT_AT);
				// (weighted by how far progress has moved)
				target = w * fromProgress + (1 - w) * fromPrior;
			} else target = fromProgress ?? fromPrior;
			if (target === null || !Number.isFinite(target)) {
				shown = null;
				return null;
			}

			if (shown === null) shown = target;
			else {
				const decayed = Math.max(0, shown - dt);
				if (target <= decayed) shown = decayed + SMOOTHING * (target - decayed);
				else if (target > decayed * RISE_FACTOR + RISE_SLACK_MS)
					shown = decayed + RISE_SMOOTHING * (target - decayed); // a genuine slowdown
				else shown = decayed; // steady progress: the remainder only falls
			}
			if (shown > MAX_REMAINING_MS) return null;
			return { remainingMs: shown, text: remainingText(shown) };
		},
	};
}

/** Whether a working indicator is visible yet: quick operations do not flash it (choice 3). */
export const INDICATOR_DELAY_MS = 400;
export const indicatorVisible = (elapsedMs: number, delayMs = INDICATOR_DELAY_MS): boolean =>
	elapsedMs >= delayMs;

/** True when an operation has run clearly past its prior (and the quiet period): "taking longer than expected" fits, a number does not. */
export const overranPrior = (elapsedMs: number, priorMs: number | null | undefined): boolean =>
	priorMs !== null &&
	priorMs !== undefined &&
	priorMs > 0 &&
	elapsedMs > Math.max(QUIET_MS, priorMs * PRIOR_OVERRUN);
